import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import QuickAddMenu from '../../components/ui/QuickAddMenu';
import SEO from '../../components/SEO';
import TeamMemberTable from './components/TeamMemberTable';
import TeamMemberModal from './components/TeamMemberModal';
import AddTeamMemberModal from './components/AddTeamMemberModal';
import ActivityLogPanel from './components/ActivityLogPanel';
import RolePermissionsPanel from './components/RolePermissionsPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PermissionDenied from '../../components/ui/PermissionDenied';
import { usePermissions } from '../../hooks/usePermissions';
import {
  getTeamMembers,
  getActivityLogs,
  getPermissions,
  getRolePermissions
} from '../../services/teamService';

const TeamMembers = () => {
  const { user, loading: authLoading } = useAuth();
  const { canManageTeam, canViewAuditLog, loading: permLoading } = usePermissions();
  const [teamMembers, setTeamMembers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState('members');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (!authLoading && user) {
      loadData();
    }
  }, [authLoading, user]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [membersData, logsData, permissionsData] = await Promise.all([
        getTeamMembers(),
        getActivityLogs({ limit: 100 }),
        getPermissions()
      ]);

      setTeamMembers(membersData);
      setActivityLogs(logsData);
      setPermissions(permissionsData);

      const roles = ['admin', 'operator', 'viewer'];
      const rolePermsData = {};
      await Promise.all(
        roles?.map(async (role) => {
          const perms = await getRolePermissions(role);
          rolePermsData[role] = perms;
        })
      );
      setRolePermissions(rolePermsData);
    } catch (err) {
      console.error('Error loading team data:', err);
      setError('Failed to load team data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMemberClick = (member) => setSelectedMember(member);
  const handleCloseModal = () => setSelectedMember(null);
  const handleMemberUpdated = () => { loadData(); setSelectedMember(null); };
  const handleMemberAdded = () => { setShowAddModal(false); loadData(); };

  const filteredMembers = teamMembers?.filter(member => {
    const matchesSearch =
      member?.fullName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      member?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      member?.department?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesRole = roleFilter === 'all' || member?.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || member?.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const activeCount = teamMembers?.filter(m => m?.status === 'active')?.length;
  const pendingCount = teamMembers?.filter(m => m?.status === 'pending')?.length;

  const tabs = [
    { id: 'members', label: 'Team Members', icon: 'Users', count: teamMembers?.length },
    ...(canViewAuditLog ? [{ id: 'activity', label: 'Activity Logs', icon: 'Activity', count: activityLogs?.length }] : []),
    ...(canViewAuditLog ? [{ id: 'permissions', label: 'Role Permissions', icon: 'Shield', count: permissions?.length }] : []),
  ];

  if (authLoading || loading || permLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="main-content">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading team data...</p>
            </div>
          </div>
        </main>
        <QuickAddMenu />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Team Members — ADU Construction Team Management"
        description="Manage your ADU construction team. Assign roles, track activity, set permissions, and invite team members to collaborate on ADU projects with ADU Flow."
        canonical="/team-members"
        keywords="team management, ADU construction team, role permissions, team collaboration"
        noIndex={true}
      />
      <Header />

      <main className="main-content">
        <div className="container mx-auto px-4 py-6 md:py-8">
          {/* Page Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Team Management</h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Manage team members, roles, and permissions
                </p>
              </div>
              {canManageTeam && (
                <Button
                  variant="default"
                  iconName="UserPlus"
                  iconPosition="left"
                  onClick={() => setShowAddModal(true)}
                >
                  Add Team Member
                </Button>
              )}
            </div>

            {/* Stats Row */}
            {teamMembers?.length > 0 && (
              <div className="flex flex-wrap gap-4 mt-4">
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-2 h-2 rounded-full bg-success" />
                  <span className="text-muted-foreground">{activeCount} Active</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-2 h-2 rounded-full bg-warning" />
                  <span className="text-muted-foreground">{pendingCount} Pending</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="w-2 h-2 rounded-full bg-muted-foreground" />
                  <span className="text-muted-foreground">{teamMembers?.length - activeCount - pendingCount} Inactive</span>
                </div>
              </div>
            )}
          </div>

          {/* Error State */}
          {error && (
            <div className="mb-6 flex items-center gap-3 p-4 bg-error/10 border border-error/20 rounded-xl">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0" />
              <p className="text-sm text-error flex-1">{error}</p>
              <button onClick={loadData} className="text-sm text-error underline hover:no-underline">
                Retry
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="border-b border-border mb-6">
            <div className="flex overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                  {tab?.count !== undefined && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                      {tab?.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'members' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="bg-card rounded-xl p-4 md:p-6 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Search</label>
                    <div className="relative">
                      <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="Search by name, email, or department..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e?.target?.value)}
                        className="w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Role</label>
                    <select
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e?.target?.value)}
                      className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="all">All Roles</option>
                      <option value="admin">Admin</option>
                      <option value="operator">Operator</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e?.target?.value)}
                      className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Team Members Table */}
              <TeamMemberTable
                members={filteredMembers}
                onMemberClick={handleMemberClick}
                onRefresh={loadData}
                canManageTeam={canManageTeam}
              />
            </div>
          )}

          {activeTab === 'activity' && canViewAuditLog && (
            <ActivityLogPanel logs={activityLogs} onRefresh={loadData} />
          )}

          {activeTab === 'permissions' && canViewAuditLog && (
            <RolePermissionsPanel
              permissions={permissions}
              rolePermissions={rolePermissions}
            />
          )}

          {(activeTab === 'activity' || activeTab === 'permissions') && !canViewAuditLog && (
            <PermissionDenied message="Activity logs and role permissions are only visible to admins and operators." className="mt-4" />
          )}
        </div>
      </main>

      <QuickAddMenu />

      {/* Add Team Member Modal */}
      {showAddModal && canManageTeam && (
        <AddTeamMemberModal
          onClose={() => setShowAddModal(false)}
          onAdded={handleMemberAdded}
        />
      )}

      {/* Edit Team Member Modal */}
      {selectedMember && (
        <TeamMemberModal
          member={selectedMember}
          onClose={handleCloseModal}
          onUpdate={handleMemberUpdated}
          canManageTeam={canManageTeam}
        />
      )}
    </div>
  );
};

export default TeamMembers;